import { useEffect, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Snippet } from '@snippits/shared';
import { deleteSnippet, getSnippetImageUrl, listMySnippets } from '../lib/snippets';
import { useAuth } from '../lib/useAuth';

export default function Dashboard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [snippets, setSnippets] = useState<Snippet[] | null>(null);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status === 'signed-out') {
      navigate('/signin?next=/app', { replace: true });
    }
  }, [auth.status, navigate]);

  useEffect(() => {
    if (auth.status !== 'signed-in') return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listMySnippets(auth.user.uid);
        if (cancelled) return;
        setSnippets(list);
        // Resolve thumbnails in parallel.
        const entries = await Promise.all(
          list.map(async (s) => [s.id, await getSnippetImageUrl(s)] as const),
        );
        if (!cancelled) {
          setThumbs(Object.fromEntries(entries));
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  if (auth.status === 'loading') {
    return (
      <div className="mx-auto max-w-5xl px-6 py-16">
        <p className="text-sm text-neutral-400">Loading…</p>
      </div>
    );
  }

  if (auth.status === 'signed-out') return null;

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <h1 className="text-3xl font-semibold tracking-tight">Your snippits</h1>

      {snippets === null && !error && (
        <p className="mt-8 text-sm text-neutral-400">Loading your snippits…</p>
      )}

      {error && <p className="mt-8 text-sm text-red-400">{error}</p>}

      {snippets?.length === 0 && (
        <p className="mt-8 text-sm text-neutral-400">
          No snippits yet.{' '}
          <Link to="/install" className="text-brand-500 hover:underline">
            Install the extension
          </Link>{' '}
          and capture your first one.
        </p>
      )}

      {snippets && snippets.length > 0 && (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {snippets.map((s) => (
            <SnippetCard
              key={s.id}
              snippet={s}
              thumbUrl={thumbs[s.id]}
              onDeleted={() => setSnippets((prev) => prev?.filter((x) => x.id !== s.id) ?? null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SnippetCard({
  snippet,
  thumbUrl,
  onDeleted,
}: {
  snippet: Snippet;
  thumbUrl?: string;
  onDeleted: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const onDelete = async (e: MouseEvent) => {
    e.preventDefault();
    if (!window.confirm('Delete this snippet?')) return;
    setDeleting(true);
    try {
      await deleteSnippet(snippet);
      onDeleted();
    } catch {
      setDeleting(false);
    }
  };

  const onCopy = async (e: MouseEvent) => {
    e.preventDefault();
    await navigator.clipboard.writeText(`${window.location.origin}/s/${snippet.id}`);
  };

  return (
    <Link
      to={`/s/${snippet.id}`}
      className="group relative block overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 hover:border-neutral-700"
    >
      <div className="aspect-video bg-neutral-950">
        {thumbUrl && (
          <img src={thumbUrl} alt={snippet.title} className="h-full w-full object-contain" />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
        <span className="truncate text-neutral-300">{snippet.title}</span>
        <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            onClick={onCopy}
            className="rounded px-1.5 py-0.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
            title="Copy link"
          >
            Copy
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="rounded px-1.5 py-0.5 text-neutral-400 hover:bg-red-950 hover:text-red-300 disabled:opacity-50"
            title="Delete"
          >
            Delete
          </button>
        </div>
      </div>
    </Link>
  );
}
